import React, {
  forwardRef,
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import Select, { ActionMeta, components } from 'react-select';

import CreatableSelect from 'react-select/creatable';
import styles from './SelectInput.module.scss';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxOutlinedIcon from '@material-ui/icons/CheckBoxOutlined';
import { Controller } from 'react-hook-form';
import { _t } from 'app/utils/i18n';
interface Option {
  default?: unknown;
  label: any;
  value: string | number;
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  options: Array<Option>;
  isMulti?: boolean;
  value?: null | Option;
  placeholder?: string;
  position?: string;
  icon?: JSX.Element;
  label?: string;
  onChange?: (value: any, actionMeta?: ActionMeta<Option>) => void;
  error?: any;
  withValidations?: boolean;
  name: string;
  resetOtherValue?: () => void;
  control?: any;
  isEdit?: boolean;
  disabled?: boolean;
  showDefaultValue?: boolean;
  isCreatable?: boolean;
  showInitialValue?: boolean;
  inSource?: boolean;
  bulkAction?: boolean;
  customStyle?: boolean;
  hint?: string;
  required?: boolean;
  width?: string;
  ref?: any;
}

const SelectInput: FunctionComponent<Props> = forwardRef(
  (
    {
      options,
      value,
      placeholder,
      isMulti,
      icon,
      label,
      hint,
      onChange,
      position,
      error,
      withValidations,
      name,
      resetOtherValue,
      control,
      isEdit,
      disabled,
      showDefaultValue,
      isCreatable,
      showInitialValue,
      inSource,
      bulkAction,
      customStyle,
      required,
      width,
      ...rest
    },
    customRef
  ) => {
    const [optionsArray, setOptionsArray] = useState(options);
    const [currentInputValue, setCurrentInputValue] = useState<any>(value);

    const multiOnSelect = (item: any) => {
      const removeOptionsFromArray = options?.filter((option) =>
        item?.every((i: any) => i.value !== option.value)
      );
      setOptionsArray([...item, ...removeOptionsFromArray]);
    };

    const getDefaultOptions = () => {
      if (options?.some((item) => item?.default) && showDefaultValue) {
        const removeOptionsFromArray = options?.filter(
          (option) => !option?.default
        );
        const addedOptionsToArray = options?.filter(
          (option) => option?.default
        );
        setCurrentInputValue(addedOptionsToArray);
        setOptionsArray([...addedOptionsToArray, ...removeOptionsFromArray]);
      }
    };
    useEffect(() => {
      if (!value) {
        setCurrentInputValue(null);
      } else {
        setCurrentInputValue(value);
      }
    }, [value]);

    useEffect(() => {
      setOptionsArray(options);
    }, [options]);

    useEffect(() => {
      getDefaultOptions();
    }, []);
    useEffect(() => {
      if (value && isMulti && currentInputValue) {
        multiOnSelect(currentInputValue);
      }
    }, []);
    const customStyles = {
      control: (styles: any) => ({ ...styles }),
      option: (styles: any) => ({ ...styles, maxWidth: '220px' }),
      menu: (styles: any) => ({ ...styles }),
    };

    const Option = (props: any) => {
      return (
        <div>
          <components.Option {...props}>
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxOutlinedIcon fontSize="small" />}
              checked={props.isSelected}
            />
            <label style={{ paddingInline: '10px' }} id="select-lebal">
              {props.label}
            </label>
          </components.Option>
        </div>
      );
    };
    return (
      <div
        className={styles.SelectInput}
        style={{ width }}
        data-testid="SelectInput-testid"
      >
        <div
          className={`${styles.SelectInput__Label} ${
            error?.[name]?.message ? styles.SelectInput__Label__Error : ''
          }`}
          id="select-lebal"
          data-source={inSource}
        >
          {label && (
            <label>
              {label} {required && '*'}
            </label>
          )}
          {hint && <label>{hint}</label>}
          {error ? (
            <p
              data-testid={`${name}-error-testid`}
              style={{
                color: '#ff1c00',
                fontSize: '12px',
              }}
              className={styles.errorP}
              id={`${name}-error`}
              data-source={inSource}
            >
              {typeof error === 'string' ? error : error?.[name]?.message}
            </p>
          ) : null}
        </div>
        <div
          className={`${styles.SelectInput__Input} ${
            icon && styles.iconInput
          } `}
          id={`selectInput-${name}`}
          data-testid={`selectInput-${name}`}
        >
          {icon && (
            <div
              className={styles.Icon}
              id="select-icon"
              data-testid="select-icon"
            >
              {icon}
            </div>
          )}
          {withValidations ? (
            <>
              <Controller
                name={name}
                control={control}
                render={({ field }) => {
                  const { onBlur, onChange, ref, value: formValue } = field;
                  const SelectType = isCreatable ? CreatableSelect : Select;
                  return (
                    <SelectType
                      ref={customRef ? customRef : (ref as any)}
                      isDisabled={disabled}
                      formatCreateLabel={(inputText: string) =>
                        `${_t('basic:create')} ${inputText}`
                      }
                      onBlur={onBlur}
                      onChange={(option: any) => {
                        const isArr = Array.isArray(option);
                        if (isMulti) multiOnSelect(option);
                        isEdit
                          ? onChange?.(option)
                          : onChange?.(
                              isArr
                                ? option.map((item) =>
                                    !isNaN(item.value)
                                      ? +item.value
                                      : item.value
                                  )
                                : option.value
                            );
                        resetOtherValue?.()
                        setCurrentInputValue(option);
                      }}
                      defaultValue={
                        showDefaultValue
                          ? value
                          : formValue === 0
                          ? { label: '', formValue: 0 }
                          : formValue
                      }
                      options={optionsArray}
                      value={currentInputValue}
                      className={`select-input-container ${
                        error?.[name]?.message ? 'error' : ''
                      }`}
                      menuPlacement={position ? 'top' : 'bottom'}
                      classNamePrefix="select-input"
                      isMulti={isMulti}
                      placeholder={
                        placeholder ? placeholder : _t('basic:select')
                      }
                      noOptionsMessage={() => _t('basic:no_options')}
                      hideSelectedOptions={isMulti && false}
                      closeMenuOnSelect={isMulti && false}
                      components={
                        (isMulti && { Option, ClearIndicator }) || undefined
                      }
                      controlShouldRenderValue={isMulti && false}
                      // menuIsOpen={true} //! for testing
                    />
                  );
                }}
              />
            </>
          ) : (
            <Select
              className={`select-input-container ${
                icon && styles.inlinePadding
              } ${bulkAction && styles.bulkAction}`}
              menuPlacement={position ? 'top' : 'bottom'}
              classNamePrefix="select-input"
              // options={currentOptions}
              isDisabled={disabled}
              options={optionsArray}
              isMulti={isMulti}
              // isSearchable={false}
              value={showInitialValue ? value : currentInputValue}
              placeholder={placeholder}
              onChange={(option: any) => {
                onChange?.(option);
                setCurrentInputValue(option);
                if (isMulti) multiOnSelect(option);
              }}
              noOptionsMessage={() => _t('basic:no_options')}
              hideSelectedOptions={isMulti && false}
              closeMenuOnSelect={isMulti && false}
              components={(isMulti && { Option, ClearIndicator }) || undefined}
              controlShouldRenderValue={isMulti && false}
              styles={customStyle ? customStyles : {}}
              // menuIsOpen={true} //! for testing
              ref={customRef as any}
              {...rest}
            />
          )}
        </div>
      </div>
    );
  }
);

export default SelectInput;

const CustomClearText: FunctionComponent = () => <>{_t('basic:clear')}</>;
const ClearIndicator = (props: any) => {
  const {
    children = <CustomClearText />,
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div
      {...restInnerProps}
      ref={ref}
      className="select-input__clear-indicator"
    >
      <div style={{ padding: '0px 5px', cursor: 'pointer' }}>{children}</div>
    </div>
  );
};
